import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Typography,
  Paper,
  TextField,
  Button,
  LinearProgress,
  Box,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "js-cookie";
import BaseUrl from "../Api/baseurl";

// Helper function to check password strength
const checkPasswordStrength = (password) => {
  const strength = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    digit: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const score = Object.values(strength).filter(Boolean).length;

  return { strength, score };
};

const UserChangePassword = ({ setIsLoggedIn }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({
    strength: {},
    score: 0,
  });
  const [basePath, setBasePath] = useState("/vendor");

  const navigate = useNavigate();

  // Set basePath based on user role
  useEffect(() => {
    const isSuperuser = Cookies.get("is_superuser") === "true";
    const isStaff = Cookies.get("is_staff") === "true";
    const isVendor = Cookies.get("is_vendor") === "true";
    if (isSuperuser) {
      setBasePath("/admin");
    } else if (isStaff && !isSuperuser) {
      setBasePath("/staff");
    } else if (isVendor) {
      setBasePath("/vendor");
    } else {
      setBasePath("/user");
    }
  }, []);

  // Update password strength
  useEffect(() => {
    const { strength, score } = checkPasswordStrength(newPassword);
    setPasswordStrength({ strength, score });
  }, [newPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    const validationErrors = {};

    if (!currentPassword) {
      validationErrors.currentPassword = "Current password is required.";
    }

    if (!newPassword) {
      validationErrors.newPassword = "New password is required.";
    } else {
      const { strength } = passwordStrength;
      if (!strength.length) {
        validationErrors.newPassword =
          "New password must be at least 8 characters long.";
      } else if (
        !strength.uppercase ||
        !strength.lowercase ||
        !strength.digit ||
        !strength.special
      ) {
        validationErrors.newPassword =
          "New password must include uppercase, lowercase, digit, and special character.";
      } else if (newPassword === currentPassword) {
        validationErrors.newPassword =
          "New password must be different from the current password.";
      }
    }

    if (newPassword !== confirmPassword) {
      validationErrors.confirmPassword = "Passwords do not match.";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const confirmationResult = await Swal.fire({
        title: "Update Password?",
        text: "Do you want to update your password?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      });

      if (confirmationResult.isConfirmed) {
        const token = Cookies.get("token");
        const response = await axios.post(
          `${BaseUrl}/users/change-password/`,
          {
            current_password: currentPassword,
            new_password: newPassword,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        Swal.fire({
          title: "Success",
          text: "Password updated successfully.",
          icon: "success",
          confirmButtonText: "Okay",
        });

        // Clear form fields
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");

        // Log out the user
        Cookies.remove("token");
        Cookies.remove("username");
        Cookies.remove("is_user");
        Cookies.remove("is_superuser");
        Cookies.remove("is_staff");
        Cookies.remove("is_vendor");
        Cookies.remove("status");
        Cookies.remove("roles");
        Cookies.remove("subroles");
        setIsLoggedIn(false);
        navigate(`/user/login`);
      }
    } catch (error) {
      console.error("Error updating password:", error);
      if (error.response) {
        const { data } = error.response;
        if (error.response.status === 401) {
          Swal.fire({
            icon: "warning",
            title: "Session expired",
            text: "Please log in again.",
          });
          Cookies.remove("token");
          Cookies.remove("username");
          Cookies.remove("is_superuser");
          Cookies.remove("is_staff");
          Cookies.remove("is_vendor");
          Cookies.remove("status");
          Cookies.remove("roles");
          Cookies.remove("subroles");
          navigate(`${basePath}/login`);
        } else if (error.response.status === 400) {
          setErrors({
            currentPassword: data.current_password
              ? data.current_password[0]
              : "",
            newPassword: data.new_password ? data.new_password[0] : "",
          });
        } else if (error.response.status === 405) {
          Swal.fire({
            title: "Error",
            text: "Method not allowed. Please contact support.",
            icon: "error",
          });
        }
      } else {
        Swal.fire({
          title: "Error",
          text: "There was a problem updating the password.",
          icon: "error",
          confirmButtonText: "Okay",
        });
      }
    }
  };

  // Password strength progress (0-100%)
  const strengthPercentage = (passwordStrength.score / 5) * 100;

  return (
    <div className="container mx-auto p-4 sm:p-6 w-full">
      <Paper elevation={3} className="p-8 rounded-xl bg-white w-full">
        
        <Typography
          variant="h4"
          className="font-bold text-center"
          style={{ color: "#212529" }}
        >
          Change Password
        </Typography>

        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-6 justify-center items-center mt-4">
            {/* Current Password */}
            <TextField
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              required
              variant="outlined"
              InputLabelProps={{ style: { color: "#212529" } }}
              InputProps={{ style: { color: "#212529" } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#212529" },
                  "&:hover fieldset": { borderColor: "#BF9455" },
                  "&.Mui-focused fieldset": { borderColor: "#BF9455" },
                },
                maxWidth: "32rem",
              }}
              error={!!errors.currentPassword}
              helperText={errors.currentPassword}
            />

            {/* New Password */}
            <div className="w-full max-w-lg">
              <TextField
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                required
                variant="outlined"
                InputLabelProps={{ style: { color: "#212529" } }}
                InputProps={{ style: { color: "#212529" } }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#212529" },
                    "&:hover fieldset": { borderColor: "#BF9455" },
                    "&.Mui-focused fieldset": { borderColor: "#BF9455" },
                  },
                  maxWidth: "32rem",
                }}
                error={!!errors.newPassword}
                helperText={errors.newPassword}
              />
              {/* Password Strength Indicator */}
              <Box mt={2}>
                <Typography
                  variant="caption"
                  style={{ color: "#212529" }}
                  className="mb-2 block"
                >
                  Password Strength
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={strengthPercentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "#e0e0e0",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor:
                        strengthPercentage < 40
                          ? "#FF0000"
                          : strengthPercentage < 80
                          ? "#FFA500"
                          : "#018030",
                    },
                  }}
                />
                <Box mt={1}>
                  {[
                    {
                      check: passwordStrength.strength.length,
                      text: "At least 8 characters",
                    },
                    {
                      check: passwordStrength.strength.uppercase,
                      text: "Contains uppercase letter",
                    },
                    {
                      check: passwordStrength.strength.lowercase,
                      text: "Contains lowercase letter",
                    },
                    {
                      check: passwordStrength.strength.digit,
                      text: "Contains digit",
                    },
                    {
                      check: passwordStrength.strength.special,
                      text: "Contains special character",
                    },
                  ].map((item, index) => (
                    <Typography
                      key={index}
                      variant="caption"
                      style={{
                        color: item.check ? "#018030" : "#FF0000",
                        display: "block",
                      }}
                    >
                      {item.check ? "✓" : "✗"} {item.text}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </div>

            {/* Confirm Password */}
            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              required
              variant="outlined"
              InputLabelProps={{ style: { color: "#212529" } }}
              InputProps={{ style: { color: "#212529" } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#212529" },
                  "&:hover fieldset": { borderColor: "#BF9455" },
                  "&.Mui-focused fieldset": { borderColor: "#BF9455" },
                },
                maxWidth: "32rem",
              }}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
            />

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 mt-6">
              <Button
                variant="outlined"
                style={{ borderColor: "#212529", color: "#212529" }}
                startIcon={<CancelIcon />}
                component={Link}
                to={`${basePath}/`}
                className="hover:bg-[#BF9455] hover:!text-white"
                aria-label="Cancel password change"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                style={{ backgroundColor: "#BF9455", color: "#FFFFFF" }}
                startIcon={<SaveIcon />}
                type="submit"
                className="hover:!bg-[#A07B3F]"
                aria-label="Change password"
              >
                Change Password
              </Button>
            </div>
          </div>
        </form>
      </Paper>
    </div>
  );
};

export default UserChangePassword;